import {
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Descriptions,
  Image,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Tag,
  Upload,
  message,
} from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { productTemplateApi } from '../../apis/productTemplate';
import { ProductSizeMap } from '../../constants/order';
import {
  BAG_SIZES,
  NewVariant,
  PREDEFINED_COLORS,
  TSHIRT_SIZES,
} from '../../constants/product-template';
import {
  ICreateProductTemplateRequest,
  IProductTemplate,
  IProductTemplateVariant,
  ProductType,
} from '../../interfaces/Order';
import { RootState } from '../../reducers';
import { Role } from '../../role/role';
import { PageContainer } from '../../styles/Global';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import PageHeader from '../PageHeader/PageHeader';

const ProductTemplateDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreateMode = id === 'new';

  const [template, setTemplate] = useState<IProductTemplate | null>(null);
  const [variants, setVariants] = useState<IProductTemplateVariant[]>([]);
  const [selectedVariant, setSelectedVariant] =
    useState<IProductTemplateVariant | null>(null);
  const [loading, setLoading] = useState(!isCreateMode);
  const [editingTemplate, setEditingTemplate] = useState(isCreateMode);
  const [editingVariants, setEditingVariants] = useState<Set<string>>(
    new Set()
  );
  const [tempTemplateData, setTempTemplateData] = useState({
    name: '',
    description: '',
    type: ProductType.TSHIRT,
    form: '',
    color: '',
    colorHexCode: '',
    frontTemplateUrl: '',
    backTemplateUrl: '',
  });
  const [tempVariantData, setTempVariantData] = useState<
    Record<string, { price: number; stockQuantity: number }>
  >({});

  // Create mode specific state
  const [newVariants, setNewVariants] = useState<NewVariant[]>([]);
  const [frontImageFile, setFrontImageFile] = useState<UploadFile | null>(null);
  const [backImageFile, setBackImageFile] = useState<UploadFile | null>(null);
  const [creating, setCreating] = useState(false);

  const { authUser } = useSelector((state: RootState) => state.auth);
  const isAdmin =
    authUser?.role === Role.ADMIN || authUser?.role === Role.MASTER_ADMIN;

  const fetchTemplate = useCallback(async () => {
    try {
      setLoading(true);
      const result = await productTemplateApi.getProductTemplateById(id!);
      setTemplate(result);
      setVariants(result.variants);
      // Initialize temp data with current values
      setTempTemplateData({
        name: result.name,
        description: result.description || '',
        type: result.type,
        form: result.form,
        color: result.color,
        colorHexCode: result.colorHexCode,
        frontTemplateUrl: result.frontTemplateUrl || '',
        backTemplateUrl: result.backTemplateUrl || '',
      });
      // Set first variant as default selected
      if (result.variants && result.variants.length > 0) {
        setSelectedVariant(result.variants[0]);
        // Initialize temp variant data
        const variantData: Record<
          string,
          { price: number; stockQuantity: number }
        > = {};
        result.variants.forEach((variant) => {
          variantData[variant.id] = {
            price: variant.price,
            stockQuantity: variant.stockQuantity,
          };
        });
        setTempVariantData(variantData);
      }
    } catch (error) {
      message.error('Không thể tải thông tin mẫu sản phẩm');
      console.error(error);
      navigate('/product-templates');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id && !isCreateMode) {
      fetchTemplate();
    } else if (isCreateMode) {
      // Initialize create mode
      setTempTemplateData({
        name: '',
        description: '',
        type: ProductType.TSHIRT,
        form: '',
        color: '',
        colorHexCode: '',
        frontTemplateUrl: '',
        backTemplateUrl: '',
      });
      setNewVariants([]);
    }
  }, [id, isCreateMode, fetchTemplate]);

  const handleDelete = () => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xóa mẫu sản phẩm này?',
      content: 'Hành động này không thể hoàn tác.',
      okText: 'Có, Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await productTemplateApi.deleteProductTemplate(id!);
          message.success('Xóa mẫu sản phẩm thành công');
          navigate('/product-templates');
        } catch (error) {
          message.error('Không thể xóa mẫu sản phẩm');
          console.error(error);
        }
      },
    });
  };

  const handleEditTemplate = () => {
    setEditingTemplate(true);
    // Initialize image files with existing images if they exist
    if (template) {
      if (template.frontTemplateUrl) {
        setFrontImageFile({
          uid: '-1',
          name: 'front-image',
          status: 'done',
          url: template.frontTemplateUrl,
          response: { url: template.frontTemplateUrl },
        } as UploadFile);
      }
      if (template.backTemplateUrl) {
        setBackImageFile({
          uid: '-2',
          name: 'back-image',
          status: 'done',
          url: template.backTemplateUrl,
          response: { url: template.backTemplateUrl },
        } as UploadFile);
      }
    }
  };

  const handleCancelTemplateEdit = () => {
    setEditingTemplate(false);
    // Reset image files
    setFrontImageFile(null);
    setBackImageFile(null);
    if (template) {
      setTempTemplateData({
        name: template.name,
        description: template.description || '',
        type: template.type,
        form: template.form,
        color: template.color,
        colorHexCode: template.colorHexCode,
        frontTemplateUrl: template.frontTemplateUrl || '',
        backTemplateUrl: template.backTemplateUrl || '',
      });
    }
  };

  const handleSaveTemplate = async () => {
    try {
      if (isCreateMode) {
        await handleCreateTemplate();
      } else {
        if (!template) return;

        // Use uploaded image URLs if new images were uploaded, otherwise use existing URLs
        const frontImageUrl =
          frontImageFile?.response?.url ||
          frontImageFile?.url ||
          tempTemplateData.frontTemplateUrl;
        const backImageUrl =
          backImageFile?.response?.url ||
          backImageFile?.url ||
          tempTemplateData.backTemplateUrl;

        await productTemplateApi.updateProductTemplate(template.id, {
          name: tempTemplateData.name,
          description: tempTemplateData.description,
          type: tempTemplateData.type,
          color: tempTemplateData.color,
          colorHexCode: tempTemplateData.colorHexCode,
          frontTemplateUrl: frontImageUrl,
          backTemplateUrl: backImageUrl,
          form: tempTemplateData.form,
        });

        // Update local state
        setTemplate({
          ...template,
          name: tempTemplateData.name,
          description: tempTemplateData.description,
          color: tempTemplateData.color,
          colorHexCode: tempTemplateData.colorHexCode,
          frontTemplateUrl: frontImageUrl,
          backTemplateUrl: backImageUrl,
          form: tempTemplateData.form,
        });

        // Update tempTemplateData with new URLs
        setTempTemplateData((prev) => ({
          ...prev,
          frontTemplateUrl: frontImageUrl,
          backTemplateUrl: backImageUrl,
          form: tempTemplateData.form,
        }));

        setEditingTemplate(false);
        message.success('Cập nhật thông tin mẫu sản phẩm thành công');
      }
    } catch (error) {
      message.error(
        isCreateMode
          ? 'Không thể tạo mẫu sản phẩm'
          : 'Không thể cập nhật thông tin mẫu sản phẩm'
      );
      console.error(error);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      setCreating(true);

      // Validate required fields
      if (!tempTemplateData.name.trim()) {
        message.error('Vui lòng nhập tên mẫu sản phẩm');
        return;
      }

      if (!tempTemplateData.color || !tempTemplateData.colorHexCode) {
        message.error('Vui lòng chọn màu sắc');
        return;
      }

      if (!tempTemplateData.form) {
        message.error('Vui lòng chọn kiểu dáng');
        return;
      }

      if (newVariants.length === 0) {
        message.error('Vui lòng thêm ít nhất một biến thể sản phẩm');
        return;
      }

      // Create template with all variants
      const templateData: ICreateProductTemplateRequest = {
        name: tempTemplateData.name,
        description: tempTemplateData.description,
        type: tempTemplateData.type,
        form:
          tempTemplateData.form ||
          (tempTemplateData.type === ProductType.BAG ? 'tote' : 'regular'),
        color: tempTemplateData.color,
        colorHexCode: tempTemplateData.colorHexCode,
        frontTemplateUrl: frontImageFile?.response?.url || undefined,
        backTemplateUrl: backImageFile?.response?.url || undefined,
        variants: newVariants.map((variant) => ({
          size: variant.size,
          price: variant.price,
          stockQuantity: variant.stockQuantity,
        })),
      };

      await productTemplateApi.createProductTemplate(templateData);

      message.success('Tạo mẫu sản phẩm thành công');
      navigate('/product-templates');
    } catch (error) {
      message.error('Không thể tạo mẫu sản phẩm');
      console.error(error);
    } finally {
      setCreating(false);
    }
  };

  const handleEditVariant = (variantId: string) => {
    setEditingVariants((prev) => new Set([...Array.from(prev), variantId]));
  };

  const handleCancelVariantEdit = (variantId: string) => {
    setEditingVariants((prev) => {
      const newSet = new Set(prev);
      newSet.delete(variantId);
      return newSet;
    });

    // Reset temp data for this variant
    const variant = variants.find((v) => v.id === variantId);
    if (variant) {
      setTempVariantData((prev) => ({
        ...prev,
        [variantId]: {
          price: variant.price,
          stockQuantity: variant.stockQuantity,
        },
      }));
    }
  };

  const handleSaveVariant = async (variantId: string) => {
    try {
      const variantData = tempVariantData[variantId];
      if (!variantData) return;

      await productTemplateApi.updateProductTemplateVariant(variantId, {
        price: variantData.price,
        stockQuantity: variantData.stockQuantity,
      });

      setVariants((prev) =>
        prev.map((variant) =>
          variant.id === variantId
            ? {
                ...variant,
                price: variantData.price,
                stockQuantity: variantData.stockQuantity,
              }
            : variant
        )
      );

      // Update selected variant if it's the one being edited
      if (selectedVariant?.id === variantId) {
        setSelectedVariant((prev) =>
          prev
            ? {
                ...prev,
                price: variantData.price,
                stockQuantity: variantData.stockQuantity,
              }
            : null
        );
      }

      setEditingVariants((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variantId);
        return newSet;
      });

      message.success('Cập nhật thông tin biến thể thành công');
    } catch (error) {
      message.error('Không thể cập nhật thông tin biến thể');
      console.error(error);
    }
  };

  // Create mode helper functions
  const addNewVariant = () => {
    const availableSizes =
      tempTemplateData.type === ProductType.TSHIRT ? TSHIRT_SIZES : BAG_SIZES;
    const usedSizes = newVariants.map((v) => v.size);
    const availableSize = availableSizes.find(
      (size) => !usedSizes.includes(size)
    );

    if (!availableSize) {
      message.warning('Tất cả kích cỡ đã được thêm');
      return;
    }

    const newVariant: NewVariant = {
      tempId: `temp_${Date.now()}`,
      size: availableSize,
      price: 0,
      stockQuantity: 0,
    };

    setNewVariants([...newVariants, newVariant]);
  };

  const removeNewVariant = (tempId: string) => {
    setNewVariants(newVariants.filter((v) => v.tempId !== tempId));
  };

  const updateNewVariant = (
    tempId: string,
    field: keyof Omit<NewVariant, 'tempId'>,
    value: any
  ) => {
    setNewVariants(
      newVariants.map((v) =>
        v.tempId === tempId ? { ...v, [field]: value } : v
      )
    );
  };

  const handleColorChange = (colorName: string) => {
    const color = PREDEFINED_COLORS.find((c) => c.name === colorName);
    if (color) {
      setTempTemplateData((prev) => ({
        ...prev,
        color: colorName,
        colorHexCode: color.hex,
      }));
    } else {
      // Fallback for colors not in predefined list
      console.warn('Color not found in predefined colors:', colorName);
      setTempTemplateData((prev) => ({
        ...prev,
        color: colorName,
        colorHexCode: '#000000', // Default to black if color not found
      }));
    }
  };

  const handleTypeChange = (type: ProductType) => {
    setTempTemplateData((prev) => ({
      ...prev,
      type,
      form: '', // Reset form when type changes
    }));
    setNewVariants([]); // Reset variants when type changes
  };

  const validateImageFile = (file: File): boolean => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Chỉ có thể tải lên file hình ảnh!');
      return false;
    }
    return true;
  };

  const handleImageUpload = async (info: any, imageType: 'front' | 'back') => {
    const { fileList: newFileList } = info;

    if (newFileList.length === 0) {
      if (imageType === 'front') {
        setFrontImageFile(null);
      } else {
        setBackImageFile(null);
      }
      return;
    }

    // Get the latest file
    const latestFile = newFileList[newFileList.length - 1];

    if (latestFile.originFileObj) {
      // Validate file before upload
      if (!validateImageFile(latestFile.originFileObj)) {
        if (imageType === 'front') {
          setFrontImageFile(null);
        } else {
          setBackImageFile(null);
        }
        return;
      }

      try {
        // Update file list with uploading status
        const updatedFile = {
          ...latestFile,
          status: 'uploading' as const,
        };

        if (imageType === 'front') {
          setFrontImageFile(updatedFile);
        } else {
          setBackImageFile(updatedFile);
        }

        const formData = new FormData();
        formData.append('file', latestFile.originFileObj);

        const imageUrl = await productTemplateApi.uploadProductTemplateImage(
          formData
        );

        // Update file list with success status and URL
        const successFile = {
          ...latestFile,
          status: 'done' as const,
          url: imageUrl,
          response: { url: imageUrl }, // For compatibility with existing code
        };

        if (imageType === 'front') {
          setFrontImageFile(successFile);
        } else {
          setBackImageFile(successFile);
        }

        message.success(
          `Tải lên hình ảnh ${
            imageType === 'front' ? 'mặt trước' : 'mặt sau'
          } thành công!`
        );
      } catch (error) {
        console.error('Image upload error:', error);
        const errorMessage =
          (error as any)?.message ||
          `Tải lên hình ảnh ${
            imageType === 'front' ? 'mặt trước' : 'mặt sau'
          } thất bại. Vui lòng thử lại.`;
        message.error(errorMessage);

        // Update file list with error status
        const errorFile = {
          ...latestFile,
          status: 'error' as const,
        };

        if (imageType === 'front') {
          setFrontImageFile(errorFile);
        } else {
          setBackImageFile(errorFile);
        }
      }
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner />
      </PageContainer>
    );
  }

  if (!template && !isCreateMode) {
    return (
      <PageContainer>
        <div>Không tìm thấy mẫu sản phẩm</div>
      </PageContainer>
    );
  }

  const getStockStatus = (stock: number) => {
    if (stock > 50) return { color: 'green', text: 'Còn hàng' };
    if (stock > 20) return { color: 'orange', text: 'Sắp hết hàng' };
    if (stock > 0) return { color: 'red', text: 'Rất ít hàng' };
    return { color: 'red', text: 'Hết hàng' };
  };

  const handleVariantChange = (variantId: string) => {
    const variant = variants.find((v) => v.id === variantId);
    if (variant) {
      setSelectedVariant(variant);
    }
  };

  const stockStatus = selectedVariant
    ? getStockStatus(selectedVariant.stockQuantity)
    : getStockStatus(
        variants.reduce((acc, variant) => acc + variant.stockQuantity, 0)
      );

  return (
    <PageContainer>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/product-templates')}
        >
          Quay lại danh sách mẫu sản phẩm
        </Button>
      </div>
      <PageHeader
        title={
          isCreateMode
            ? 'Tạo mẫu sản phẩm mới'
            : `${template?.name} - Mẫu sản phẩm`
        }
        customBlock={
          isAdmin ? (
            <Space>
              {!isCreateMode && (
                <Button
                  type="default"
                  icon={<EditOutlined />}
                  onClick={handleEditTemplate}
                  disabled={editingTemplate}
                >
                  {editingTemplate ? 'Đang chỉnh sửa...' : 'Chỉnh sửa'}
                </Button>
              )}
              {(editingTemplate || isCreateMode) && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveTemplate}
                  loading={creating}
                  disabled={
                    isCreateMode
                      ? !tempTemplateData.name.trim() ||
                        !tempTemplateData.color ||
                        !tempTemplateData.colorHexCode ||
                        !tempTemplateData.form ||
                        newVariants.length === 0
                      : !tempTemplateData.name.trim()
                  }
                >
                  {isCreateMode ? 'Tạo mẫu sản phẩm' : 'Lưu thay đổi'}
                </Button>
              )}
            </Space>
          ) : undefined
        }
      />

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <Card
          style={{ flex: '0 0 300px' }}
          cover={
            isCreateMode || editingTemplate ? (
              <div
                style={{
                  height: 300,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  padding: 16,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{ marginBottom: 8, fontSize: '12px', color: '#666' }}
                  >
                    Hình ảnh mặt trước:
                  </div>
                  <Upload
                    name="file"
                    listType="picture-card"
                    fileList={frontImageFile ? [frontImageFile] : []}
                    onChange={(info) => handleImageUpload(info, 'front')}
                    onRemove={() => setFrontImageFile(null)}
                    maxCount={1}
                    accept="image/*"
                    beforeUpload={() => false} // Prevent auto upload
                    style={{ width: '100%' }}
                  >
                    {!frontImageFile && (
                      <div>
                        <UploadOutlined />
                        <div style={{ marginTop: 8, fontSize: '12px' }}>
                          Tải lên
                        </div>
                      </div>
                    )}
                  </Upload>
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{ marginBottom: 8, fontSize: '12px', color: '#666' }}
                  >
                    Hình ảnh mặt sau:
                  </div>
                  <Upload
                    name="file"
                    listType="picture-card"
                    fileList={backImageFile ? [backImageFile] : []}
                    onChange={(info) => handleImageUpload(info, 'back')}
                    onRemove={() => setBackImageFile(null)}
                    maxCount={1}
                    accept="image/*"
                    beforeUpload={() => false} // Prevent auto upload
                    style={{ width: '100%' }}
                  >
                    {!backImageFile && (
                      <div>
                        <UploadOutlined />
                        <div style={{ marginTop: 8, fontSize: '12px' }}>
                          Tải lên
                        </div>
                      </div>
                    )}
                  </Upload>
                </div>
              </div>
            ) : template?.frontTemplateUrl || template?.backTemplateUrl ? (
              <Image
                src={
                  template.frontTemplateUrl ?? template.backTemplateUrl ?? ''
                }
                alt={template?.name}
                style={{ height: 300, objectFit: 'cover' }}
              />
            ) : (
              <div
                style={{
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  color: '#999',
                  fontSize: '14px',
                  border: '1px dashed #d9d9d9',
                  borderRadius: '6px',
                }}
              >
                Chưa có hình ảnh mẫu
              </div>
            )
          }
        >
          <Card.Meta
            title={
              editingTemplate || isCreateMode ? (
                <Input
                  value={tempTemplateData.name}
                  onChange={(e) =>
                    setTempTemplateData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="Tên mẫu sản phẩm"
                  style={{ marginBottom: 8 }}
                />
              ) : (
                template?.name
              )
            }
            description={
              editingTemplate || isCreateMode ? (
                <Input.TextArea
                  value={tempTemplateData.description}
                  onChange={(e) =>
                    setTempTemplateData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Mô tả sản phẩm"
                  rows={3}
                />
              ) : (
                template?.description || 'Không có mô tả'
              )
            }
          />
          {editingTemplate && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Space>
                <Button size="small" onClick={handleCancelTemplateEdit}>
                  Hủy
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={handleSaveTemplate}
                  disabled={!tempTemplateData.name.trim()}
                >
                  Lưu
                </Button>
              </Space>
            </div>
          )}
        </Card>

        <Card title="Chi tiết sản phẩm" style={{ flex: 1, minWidth: 400 }}>
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Loại">
              {editingTemplate || isCreateMode ? (
                <Select
                  value={tempTemplateData.type}
                  onChange={handleTypeChange}
                  style={{ width: 200 }}
                  disabled={!isCreateMode} // Only allow changing type in create mode
                >
                  <Select.Option value={ProductType.TSHIRT}>Áo</Select.Option>
                  <Select.Option value={ProductType.BAG}>Túi</Select.Option>
                </Select>
              ) : (
                <Tag color="blue">
                  {template?.type === ProductType.TSHIRT
                    ? 'Áo'
                    : (template?.type as string).toUpperCase()}
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Kiểu dáng">
              {editingTemplate || isCreateMode ? (
                <Input
                  value={tempTemplateData.form}
                  onChange={(e) =>
                    setTempTemplateData((prev) => ({
                      ...prev,
                      form: e.target.value,
                    }))
                  }
                  style={{ width: 200 }}
                  placeholder="Nhập kiểu dáng"
                />
              ) : (
                <Tag color="cyan">
                  {template?.form?.toUpperCase() || 'KHÔNG XÁC ĐỊNH'}
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Màu sắc">
              {editingTemplate || isCreateMode ? (
                <Space>
                  <Select
                    value={tempTemplateData.color}
                    onChange={handleColorChange}
                    style={{ width: 200 }}
                    placeholder="Chọn màu sắc"
                  >
                    {PREDEFINED_COLORS.map((color) => (
                      <Select.Option key={color.name} value={color.name}>
                        <Space>
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              backgroundColor: color.hex,
                              border: '1px solid #d9d9d9',
                              borderRadius: 2,
                            }}
                          />
                          {color.name}
                        </Space>
                      </Select.Option>
                    ))}
                  </Select>
                  {tempTemplateData.colorHexCode && (
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        backgroundColor: tempTemplateData.colorHexCode,
                        border: '1px solid #d9d9d9',
                        borderRadius: 4,
                      }}
                    />
                  )}
                </Space>
              ) : (
                <Space>
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      backgroundColor: template?.colorHexCode,
                      border: '1px solid #d9d9d9',
                      borderRadius: 4,
                    }}
                  />
                  {template?.color}
                </Space>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Quản lý kích cỡ và tồn kho">
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                {isCreateMode ? (
                  <>
                    {newVariants.map((variant) => (
                      <div
                        key={variant.tempId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: 8,
                          border: '1px solid #f0f0f0',
                          borderRadius: 4,
                          backgroundColor: '#f9f9f9',
                        }}
                      >
                        <Select
                          value={variant.size}
                          onChange={(size) =>
                            updateNewVariant(variant.tempId, 'size', size)
                          }
                          style={{ width: 150 }}
                        >
                          {(tempTemplateData.type === ProductType.TSHIRT
                            ? TSHIRT_SIZES
                            : BAG_SIZES
                          ).map((size) => (
                            <Select.Option
                              key={size}
                              value={size}
                              disabled={newVariants.some(
                                (v) =>
                                  v.size === size && v.tempId !== variant.tempId
                              )}
                            >
                              {ProductSizeMap[size]}
                            </Select.Option>
                          ))}
                        </Select>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flex: 1,
                          }}
                        >
                          <span style={{ minWidth: 30 }}>Giá:</span>
                          <InputNumber
                            value={variant.price}
                            onChange={(value) =>
                              updateNewVariant(
                                variant.tempId,
                                'price',
                                value || 0
                              )
                            }
                            formatter={(value) =>
                              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            parser={(value) => Number(value!.replace(/,/g, ''))}
                            style={{ width: 120 }}
                            min={0}
                          />

                          <span style={{ minWidth: 50 }}>Tồn kho:</span>
                          <InputNumber
                            value={variant.stockQuantity}
                            onChange={(value) =>
                              updateNewVariant(
                                variant.tempId,
                                'stockQuantity',
                                value || 0
                              )
                            }
                            style={{ width: 80 }}
                            min={0}
                          />
                        </div>

                        <Button
                          type="text"
                          danger
                          onClick={() => removeNewVariant(variant.tempId)}
                          size="small"
                        >
                          Xóa
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={addNewVariant}
                      style={{ marginTop: 8 }}
                    >
                      Thêm biến thể
                    </Button>
                  </>
                ) : (
                  variants.map((variant) => {
                    const isEditing = editingVariants.has(variant.id);
                    const variantData = tempVariantData[variant.id] || {
                      price: variant.price,
                      stockQuantity: variant.stockQuantity,
                    };

                    return (
                      <div
                        key={variant.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: 8,
                          border: '1px solid #f0f0f0',
                          borderRadius: 4,
                          backgroundColor: isEditing
                            ? '#f9f9f9'
                            : 'transparent',
                        }}
                      >
                        <Tag
                          color="green"
                          style={{ minWidth: 40, textAlign: 'center' }}
                        >
                          {variant.size}
                        </Tag>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flex: 1,
                          }}
                        >
                          <span style={{ minWidth: 30 }}>Giá:</span>
                          {isEditing ? (
                            <InputNumber
                              value={variantData.price}
                              onChange={(value) =>
                                setTempVariantData((prev) => ({
                                  ...prev,
                                  [variant.id]: {
                                    ...variantData,
                                    price: value || 0,
                                  },
                                }))
                              }
                              formatter={(value) =>
                                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                              }
                              parser={(value) =>
                                Number(value!.replace(/,/g, ''))
                              }
                              style={{ width: 120 }}
                              min={0}
                            />
                          ) : (
                            <span style={{ minWidth: 80 }}>
                              {variant.price.toLocaleString('vi-VN')}₫
                            </span>
                          )}

                          <span style={{ minWidth: 50 }}>Tồn kho:</span>
                          {isEditing ? (
                            <InputNumber
                              value={variantData.stockQuantity}
                              onChange={(value) =>
                                setTempVariantData((prev) => ({
                                  ...prev,
                                  [variant.id]: {
                                    ...variantData,
                                    stockQuantity: value || 0,
                                  },
                                }))
                              }
                              style={{ width: 80 }}
                              min={0}
                            />
                          ) : (
                            <span style={{ minWidth: 40 }}>
                              {variant.stockQuantity}
                            </span>
                          )}
                        </div>

                        {isAdmin && (
                          <div>
                            {isEditing ? (
                              <Space size="small">
                                <Button
                                  size="small"
                                  onClick={() =>
                                    handleCancelVariantEdit(variant.id)
                                  }
                                >
                                  Hủy
                                </Button>
                                <Button
                                  type="primary"
                                  size="small"
                                  onClick={() => handleSaveVariant(variant.id)}
                                >
                                  Lưu
                                </Button>
                              </Space>
                            ) : (
                              <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => handleEditVariant(variant.id)}
                              >
                                Sửa
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </Descriptions.Item>
            {!isCreateMode && (
              <>
                <Descriptions.Item label="Ngày tạo">
                  {new Date(template!.createdAt).toLocaleString('vi-VN')}
                </Descriptions.Item>
                <Descriptions.Item label="Cập nhật lần cuối">
                  {new Date(template!.updatedAt).toLocaleString('vi-VN')}
                </Descriptions.Item>
              </>
            )}
          </Descriptions>
        </Card>
      </div>
    </PageContainer>
  );
};

export default ProductTemplateDetail;
